import React from "react";
import { useNavigate } from "react-router-dom";

const DroneOpsAbout = () => {
  const navigate = useNavigate();

  return (
    <>
      {/* Hero Section */}
      <section
        className="relative h-[75vh] sm:h-[85vh] bg-cover bg-center flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: "url('/assets/banners/droneops.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        
        <div className="relative z-10 text-center px-2">
          <div className="inline-flex items-center gap-3 bg-yellow-600/20 text-yellow-300 px-6 py-2 rounded-md mb-6 text-sm font-medium tracking-widest">
            THINK5WHYS DRONEOPS
          </div>

          <h1 className="text-lg lg:text-xl font-bold leading-tight mb-2">
            Think5Whys DroneOps
          </h1>
          
          <p className="text-sm lg:text-xl text-yellow-100 mb-2 font-light">
            Cognitive Process Platform for Drone Excellence
          </p>
          
          <p className="text-lg text-gray-100 mb-10">
            Not just an RCA tool — a complete Six Sigma brain powered by GenAI & XAI 
            that helps drone companies think, learn, and improve continuously.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 place-items-center">
            <button
              onClick={() => navigate("/rcas/add")}
              className="px-2 py-2 bg-yellow-600 hover:bg-yellow-500 text-black font-semibold rounded-md transition text-lg flex items-center gap-3"
            >
              🚀 DeepThink Lab
            </button>
            <button
              onClick={() => navigate("/tasks/add")}
              className="px-2 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 font-semibold rounded-md transition text-lg"
            >
              📖 Chronicle
            </button>
            <button
              onClick={() => navigate("/aicounselling")}
              className="px-2 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 font-semibold rounded-md transition text-lg"
            >
              🤖 AI Counselling
            </button>
          </div>

          <div className="mt-4 text-xs uppercase tracking-widest text-gray-100">
            Quantum-ready • Industry-agnostic • Built for real drone operations
          </div>
        </div>
      </section>

      {/* Intro Quote */}
      <div className="bg-zinc-900 py-12 border-b border-zinc-800">
        <div className="px-2 text-center">
          <p className="text-xl italic text-gray-50">
            “Think5Whys for DroneOps is not just an RCA tool — it's a cognitive process platform 
            that uses GenAI and XAI to help drone companies think, learn and improve continuously. 
            Quantum-ready and industry-agnostic, it's the Six Sigma brain behind every smart drone mission.”
          </p>
          <p className="mt-4 text-yellow-300 font-medium">
            — Sanjeev Kumar, Founder & Dr. Vijay Vichare, Cofounder, Think5Whys Global DeepTech Solutions
          </p>
        </div>
      </div>

      {/* Courses Section */}
      <section id="courses" className="py-20 px-6 md:px-12 bg-zinc-950">
        <div>
          <h2 className="text-4xl font-bold text-center mb-4 text-white">DroneOps Learning Programs</h2>
          <p className="text-center text-gray-100 mb-2">
            Master drone operations, regulatory compliance, and process excellence
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
            {[
              {
                id: 1,
                title: "Digital Law & Legal Technology for Drones",
                description: "Cyber law, DGCA regulations, digital evidence, NPNT compliance & automation.",
                img: "/api/downloads/thinkdscut/bharatbrain/courses/digital-law.jpg"
              },
              {
                id: 2,
                title: "Think5Whys for Drone Operations",
                description: "Root cause analysis, process improvement, and Six Sigma for drone missions.",
                img: "/api/downloads/thinkdscut/bharatbrain/courses/legal-reasoning.jpg"
              },
              {
                id: 3,
                title: "Advanced DroneOps Certification",
                description: "Mission planning, fleet management, AI integration, and compliance mastery.",
                img: "/api/downloads/thinkdscut/bharatbrain/courses/cle-plus.jpg"
              }
            ].map((course) => (
              <div key={course.id} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-yellow-500/30 transition group">
                <img 
                  src={course.img} 
                  alt={course.title} 
                  className="w-full h-56 object-cover group-hover:scale-105 transition duration-500"
                />
                <div className="p-8">
                  <h3 className="text-lg lg:text-xl font-semibold text-white mb-3">{course.title}</h3>
                  <p className="text-gray-100 mb-6">{course.description}</p>
                  <button 
                    onClick={() => navigate(`/courses/${course.id}`)}
                    className="px-6 py-3 bg-yellow-600 hover:bg-yellow-500 text-black rounded-md text-sm font-medium transition"
                  >
                    Enroll Now →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Faculty Section */}
      <section id="faculty" className="py-20 px-6 md:px-12 bg-zinc-900">
        <div>
          <h2 className="text-xl lg:text-lg lg:text-xl font-bold text-center mb-4 text-white">Our Experts</h2>
          <p className="text-center text-gray-100 mb-12">Learn from leaders in law, technology, and drone operations</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              {
                name: "Ajay Srivastava",
                title: "Veteran Legal Counsel, Advocate, Supreme Court of India",
                img: "/api/downloads/thinkdscut/bharatbrain/faculty/nofacultyimage.jpg"
              },
              {
                name: "Sanjeev Kumar",
                title: "Digital, AI & Cybersecurity Expert | Founder, Think5Whys Global",
                img: "/api/downloads/thinkdscut/bharatbrain/faculty/nofacultyimage.jpg"
              },
              {
                name: "U. Swaminathan",
                title: "Cyberlaw Expert, Advocate, Madras High Court",
                img: "/api/downloads/thinkdscut/bharatbrain/faculty/nofacultyimage.jpg"
              }
            ].map((faculty, idx) => (
              <div key={idx} className="bg-zinc-950 border border-zinc-800 rounded-md p-2 text-center hover:border-yellow-500/30 transition">
                <div className="w-40 h-40 mx-auto mb-6 bg-zinc-800 rounded-2xl overflow-hidden">
                  <img 
                    src={faculty.img} 
                    alt={faculty.name} 
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2">{faculty.name}</h3>
                <p className="text-yellow-400 text-sm leading-relaxed">{faculty.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why DroneOps Section */}
      <section id="whyus" className="py-20 px-6 md:px-12 bg-gradient-to-br from-zinc-950 via-yellow-950/20 to-zinc-950">
        <div>
          <h2 className="text-4xl font-bold text-center mb-2 text-white">Why Choose DroneOps?</h2>
          <p className="text-center text-gray-100 mb-4">
            The only platform that combines drone operations with Six Sigma thinking and AI-powered continuous improvement
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            {[
              { emoji: "🎯", title: "Root Cause Excellence", desc: "Turn every mission failure into permanent process improvement using Think5Whys" },
              { emoji: "🤖", title: "GenAI + XAI Integration", desc: "Auto-generate RCA reports, SOPs, and explainable insights from drone data" },
              { emoji: "📜", title: "DGCA & Regulatory Ready", desc: "Built-in compliance workflows for NPNT, Digital Sky, and audit-ready documentation" },
              { emoji: "⚙️", title: "Pre-built Drone Templates", desc: "Pre-flight checklists, post-flight analysis, fleet maintenance & sector-specific workflows" },
              { emoji: "📈", title: "Continuous Improvement Loop", desc: "DMAIC + Kaizen built into every drone operation" },
              { emoji: "🔗", title: "Easy Integration", desc: "Plug into DroneDeploy, Pix4D, Skydio and other popular drone platforms" },
            ].map((item, idx) => (
              <div key={idx} className="bg-zinc-900/70 border border-zinc-700 p-8 rounded-3xl hover:border-yellow-500/40 transition group">
                <div className="text-6xl mb-6 group-hover:scale-110 transition">{item.emoji}</div>
                <h4 className="text-lg lg:text-xl font-semibold text-white mb-4">{item.title}</h4>
                <p className="text-gray-100 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision, Mission & Values */}
      <section className="py-4 px-2 bg-zinc-950">
        <div className="space-y-4">
          <div className="text-center">
            <h2 className="tetx-xl lg:text-lg lg:text-xl font-bold text-white mb-3">Vision • Mission • Values</h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
          </div>

          {/* Vision */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-yellow-500/10 p-10 rounded-3xl">
            <h3 className="text-lg lg:text-xl font-semibold mb-6 flex items-center gap-4">
              🌟 Vision
            </h3>
            <p className="text-lg text-gray-50 leading-relaxed">
              To become the world’s premier cognitive platform for drone operations — 
              empowering every drone company to achieve process excellence, regulatory confidence, 
              and continuous innovation through Think5Whys methodology.
            </p>
          </div>

          {/* Mission */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-yellow-500/10 p-10 rounded-3xl">
            <h3 className="text-lg lg:text-xl font-semibold mb-6 flex items-center gap-4">
              🎯 Mission
            </h3>
            <ul className="space-y-4 text-lg text-gray-50">
              <li className="flex gap-4"><span className="text-yellow-400">→</span> Make Six Sigma thinking accessible to every drone pilot and operator</li>
              <li className="flex gap-4"><span className="text-yellow-400">→</span> Bridge the gap between drone data and actionable process improvement</li>
              <li className="flex gap-4"><span className="text-yellow-400">→</span> Integrate GenAI & XAI to accelerate root cause analysis and decision making</li>
              <li className="flex gap-4"><span className="text-yellow-400">→</span> Build a quantum-ready foundation for the next generation of drone intelligence</li>
            </ul>
          </div>

          {/* Core Values */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-yellow-500/10 p-10 rounded-3xl">
            <h3 className="text-lg lg:text-xl font-semibold mb-8 flex items-center gap-4">
              💡 Core Values
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-lg">
              {[
                "Process-First Thinking",
                "Explainable AI & Trust",
                "Regulatory Excellence",
                "Continuous Kaizen",
                "Human + Machine Collaboration",
                "Safety & Accountability"
              ].map((value, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-2 h-2 mt-2.5 rounded-md bg-yellow-400 flex-shrink-0"></div>
                  <span className="text-gray-50">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DroneOpsAbout;